import React from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import theme from "../../../../../theme/themeThree";
import { FaCartShopping, FaCheck } from "react-icons/fa6";
import { Button } from "MVVM/Components/Button";
import { getCardTitle, isPurchaseOnly } from "../../../../Utilities/Studio";
import { Headline } from "../../../Text";
import { AddCard } from "../AddCard";
import { TagRow } from "../TagRow";
import { SeriesRow } from "../../../../Views/CreateMontage/Components/Loadouts/Components/SeriesRow";
import { ReactComponent as Mythic } from "../../../../Assets/Tiers/Badges/mythic.svg";
import { ReactComponent as Legendary } from "../../../../Assets/Tiers/Badges/legendary.svg";
import { ReactComponent as Rare } from "../../../../Assets/Tiers/Badges/rare.svg";
import { ReactComponent as Premium } from "../../../../Assets/Tiers/Badges/premium.svg";
import { ReactComponent as Standard } from "../../../../Assets/Tiers/Badges/standard.svg";
import { ReactComponent as EssentialsIconSVG } from "../../../../Assets/Avatar/Icons/essentials.svg";
import { Tooltip } from "../../../../Components/Tooltip";
import { RarityTooltipContent } from "../../../Tooltip/Components/RarityToolTIp";
import { display, justifyContent } from "styled-system";

export const Controls = ({
  handleCardClick,
  card,
  handleToggle,
  onQuery,
  isActive,
  outsideControl,
  loadout,
  tooltipContent,
  setModal,
  hideToolTip = true,
  hasScope,
  displayRarity,
  owned = false,
}: any) => {
  const priced = isPurchaseOnly(card);
  const isOverhauled = displayRarity === "ESSENTIALS" || displayRarity === null;
  const rarity = isOverhauled
    ? card.ui.rarity
    : displayRarity ?? card.ui.rarity;

  return (
    <>
      <StyledDescription
        flexDirection="column"
        justifyContent="space-between"
        onClick={() => {
          handleCardClick(card);
        }}
      >
        <GradientBox p={[3, 3, 4, 4]} rarity={rarity}>
          <Flex justifyContent="space-between" alignItems={"center"} mb={2}>
            <Headline
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {getCardTitle(card)}{" "}
              <Flex flexShrink={0} mt={2} ml={2}>
                {priced ? (
                  <PricePill owned={owned} price={card.ui.purchasePrice} />
                ) : displayRarity === "ESSENTIALS" ? (
                  <StyledEssentialsIcon />
                ) : displayRarity === null ? null : (
                  <Tooltip
                    position={{
                      bottom: "3px",
                      right: "35px",
                    }}
                    hide={false}
                    align="right"
                    theme={getVariant(rarity)}
                    timeout={true}
                    tooltipContent={<RarityTooltipContent rarity={rarity} />}
                  >
                    <Box
                      onClick={(e) => {
                        setModal({
                          type: rarity,
                          data: { card: card, hasScope: hasScope },
                        });
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <TierBadge rarity={rarity} />
                    </Box>
                  </Tooltip>
                )}
              </Flex>
            </Headline>

            {priced && !owned ? (
              <BuyButton card={card} setModal={setModal} />
            ) : (
              <Box>
                <Tooltip
                  position={{
                    top: "-45px",
                    padding: "7px",
                    right: "0px",
                  }}
                  hide={hideToolTip}
                  theme="helpTooltip"
                  align="right"
                  timeout={true}
                  tooltipContent={tooltipContent}
                >
                  <AddCard
                    card={card}
                    handleToggle={handleToggle}
                    key={card._id}
                    isActive={isActive}
                    outsideControl={outsideControl}
                  />
                </Tooltip>
              </Box>
            )}
          </Flex>
          <Flex
            flexDirection="row"
            justifyContent={"space-between"}
            alignItems="center"
          >
            <Flex flexDirection="row">
              <TagRow card={card} onQuery={onQuery} />{" "}
            </Flex>
          </Flex>
        </GradientBox>

        {loadout && <SeriesRow {...loadout} />}
      </StyledDescription>
    </>
  );
};

const PricePill = ({ owned, price }: { owned: boolean; price: number }) => {
  if (owned) {
    return (
      <StyledPill color={theme.colors.envy} bg={theme.colors.envyAlpha20}>
        <FaCheck size={10} />
        <Text variant="text.labelXSmall">Owned</Text>
      </StyledPill>
    );
  }
  return (
    <StyledPill color={theme.colors.goldStar} bg={GOLD_ALPHA}>
      <Text variant="text.labelXSmall">${price.toFixed(2)}</Text>
    </StyledPill>
  );
};

const BuyButton = ({ card, setModal }: any) => {
  const openBuyModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setModal({ type: "buyCard", data: { card }, cardTitle: card.ui.title });
  };

  return (
    <Button
      className="roundedFilled"
      size="tiny"
      flex={true}
      style={{
        minWidth: "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        borderRadius: "99px",
        padding: "0px 10px",
        backgroundColor: theme.colors.goldStar,
        color: theme.colors.midnight,
      }}
      onClick={openBuyModal}
    >
      <FaCartShopping size={11} />
      <Text variant="text.labelXSmall">Buy</Text>
    </Button>
  );
};

const GOLD_ALPHA = "rgba(255, 215, 0, 0.15)";

const StyledPill = styled(Flex)`
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 99px;
  white-space: nowrap;
`;

const TierBadge = ({ rarity }: any) => {
  //@chris manages badges
  if (rarity === 500) return <StyledMythic />; //mythic
  if (rarity === 400) return <StyledLegendary />; //legendary
  if (rarity === 300) return <StyledRare />; //rare
  if (rarity === 200) return <StyledPremium />; //premium
  if (rarity === 100) return <StyledStandard />; //standard
  return <></>;
};

const getVariant = (rarity: any) => {
  if (rarity === 500) return "mythicTooltip"; //mythic
  if (rarity === 400) return "legendaryTooltip"; //legendary
  if (rarity === 300) return "rareTooltip"; //rare
  if (rarity === 200) return "premiumTooltip"; //premium
  if (rarity === 100) return "standardTooltip"; //standard
  return "";
};

const GradientBox: any = styled(Box)`
  background: ${(props: any) => {
    if (props.rarity === 500) {
      return `linear-gradient(180deg, ${props.theme.colors.mythicAlpha10} 0%, rgba(0,0,0,0) 70%);`;
    }
    //@Chris, manages gradient in controls area
    //legendary, todo
    if (props.rarity === 400) {
      return `linear-gradient(180deg, ${props.theme.colors.legendaryAlpha10} 0%, rgba(0,0,0,0) 70%);`;
    }

    //rare, todo
    if (props.rarity === 300) {
      return `linear-gradient(180deg, ${props.theme.colors.rareAlpha10} 0%, rgba(0,0,0,0) 70%);`;
    }

    //premium, todo
    if (props.rarity === 200) {
      return `linear-gradient(180deg, ${props.theme.colors.premiumAlpha20} 0%, rgba(0,0,0,0) 70%);`;
    }

    //standard, todo
    if (props.rarity === 100) {
      return `linear-gradient(180deg, ${props.theme.colors.midnightAlpha30} 0%, rgba(0,0,0,0) 70%);`;
    }

    return `linear-gradient(180deg, ${props.theme.colors.midnightAlpha30} 0%, rgba(0,0,0,0) 70%);`;
  }};
`;

const StyledMythic = styled(Mythic)`
  height: 32px;
  width: 32px;
`;

const StyledLegendary = styled(Legendary)`
  height: 32px;
  width: 32px;
`;

const StyledRare = styled(Rare)`
  height: 34px;
  width: 34px;
`;

const StyledPremium = styled(Premium)`
  height: 32px;
  width: 32px;
`;

const StyledStandard = styled(Standard)`
  height: 32px;
  width: 32px;
`;

const StyledEssentialsIcon = styled(EssentialsIconSVG)`
  height: 32px;
  width: 32px;
`;

const StyledDescription = styled(Flex)`
  background: ${theme.colors.midnightAlpha50}};
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
`;
